<template>
  <div 
    id="app" 
    :class="{
      'routing': routing
    }" 
    @click="() => {/* Blur on ios */}"
  >
    <a href="#main" class="sr-only sr-focusable">{{ $t('action.skipToMainContent') }}</a>
    <div aria-live="polite" id="accessibility-message"></div>
    <template v-if="!whoops">
      <announcement-bar v-if="!inAnnouncmentView"></announcement-bar>
      <main-header></main-header>
      <progress-bar></progress-bar>
      <slider-modal></slider-modal>
      <geo-info-modal></geo-info-modal>
      <modal-manager></modal-manager>
    </template>
    <main id="main"><router-view></router-view></main>
    <template v-if="!whoops">
      <main-footer></main-footer>
      <chatbot-link v-if="($route.name || '').startsWith('Home_')"></chatbot-link>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import AnnouncementBar from '@/components/AnnouncementBar';
import ProgressBar from '@/components/ProgressBar';
import MainHeader from '@/components/MainHeader';
import MainFooter from '@/components/MainFooter';
import ChatbotLink from '@/components/ChatbotLink';
import SliderModal from '@/components/SliderModal';
import GeoInfoModal from '@/components/GeoInfoModal';
import ModalManager from '@/components/ModalManager';
import WhoopsView from '@/views/WhoopsView';
import PageNotFoundView from '@/views/PageNotFoundView';
import supportLocale from '@/../server/constants/supportLocale';

const HOME_LOCALES = supportLocale.map(({ locale }) => locale.toLowerCase());
const FA_FULL_OVERRIDE_LINK_ID = 'fa-full-font-face-override';
const FA_FULL_OVERRIDE_HREF = 'https://media.macaotourism.gov.mo/uploads/mgto_fonts/css/fontawesome-full-font-face.css';

export default {
  name: 'app',
  uit() {
    return [
      ['action', ['skipToMainContent', 'pause', 'play', 'mute', 'unmute']],
      ['common', ['nextPage', 'previousPage']]
    ]
  },
  components: {
    AnnouncementBar,
    ProgressBar,
    MainHeader,
    MainFooter,
    ChatbotLink,
    SliderModal,
    GeoInfoModal,
    ModalManager
  },
  asyncData({ i18n, store, route }) {
    return Promise.all([
      store.dispatch('uit/FETCH_UIT', { component: this, i18n }),
      store.dispatch('socialmedia/FETCH_SUBSCRIPTION', { locale: i18n.locale }),
      ...[
        AnnouncementBar, 
        GeoInfoModal, 
        MainHeader, 
        MainFooter, 
        ChatbotLink,
        PageNotFoundView, 
        WhoopsView
      ].map(component => store.dispatch('uit/FETCH_UIT', { component, i18n })),
      MainHeader.asyncData({i18n, store}),
    ]);
  },
  lazyData({ i18n, store, route }) {
    if (route.name && route.name.startsWith('SomethingWrong')) return;
    
    store.dispatch('attraction/FETCH_POPULAR', { locale: i18n.locale });
    store.dispatch('banner/FETCH_FOOTER', { locale: i18n.locale });
  },
  data() {
    return {
      resizeTimer: null,
      appScrollCheckPoint: 0
    }
  },
  computed: {
    ...mapState({
      routing: state => state.ui.routing,
      freezeBody: state => state.ui.freeze,
      isMobileView: state => state.ui.isMobileView,
      showMapOnMobile: state => state.ui.showMapOnMobile,
      mobileHeaderShow: state => state.ui.mobileHeader,
      blackAndWhite: state => state.ui.blackAndWhite
    }),
    freeze() {
      return this.freezeBody || this.mobileHeaderShow;
    },
    whoops() {
      return this.$route.name && this.$route.name.startsWith('SomethingWrong');
    },
    inAnnouncmentView() {
      return this.$route.name && this.$route.name.startsWith('Announcement');
    }
  },
  mounted() {
    window.AppRouter = this.$router;
    window.matchMedia("(min-width: 768px)").addListener(this.checkIsMobileView)
    this.updateIsMobile();
    this.checkIsMobileView();
    this.ensureFullFontForNonHomeRoute(this.$route);
  },
  beforeDestroy() {
    window.AppRouter = null;
    window.matchMedia("(min-width: 768px)").removeListener(this.checkIsMobileView)
  },
  methods: {
    updateIsMobile() {
      this.$store.dispatch('ui/UPDATE_IS_MOBILE', { 
        isMobile: this.checkIsMobile() 
      })
    },
    checkIsMobileView(e) {
      let isMobileView;
      if (!e) {
        isMobileView = !window.matchMedia("(min-width: 768px)").matches;
      } else {
        isMobileView = !e.matches;
      }

      this.$store.dispatch('ui/UPDATE_IS_MOBILE_VIEW', { isMobileView });
    },
    delpayCheckIsMobileView() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(this.checkIsMobileView, 100);
    },
    isHomeRoute(route) {
      const rawPath = route && route.path ? route.path : '';
      const normalized = (rawPath || '/').replace(/\/+$/, '') || '/';
      if (normalized === '/') {
        return true;
      }
      const segments = normalized.split('/').filter(Boolean);
      return segments.length === 1 && HOME_LOCALES.includes(segments[0].toLowerCase());
    },
    ensureFullFontForNonHomeRoute(route) {
      if (this.$isServer || this.isHomeRoute(route)) {
        return;
      }
      if (document.getElementById(FA_FULL_OVERRIDE_LINK_ID)) {
        return;
      }
      const fullFontLink = document.createElement('link');
      fullFontLink.id = FA_FULL_OVERRIDE_LINK_ID;
      fullFontLink.rel = 'stylesheet';
      fullFontLink.href = FA_FULL_OVERRIDE_HREF;
      document.head.appendChild(fullFontLink);
    },
    handleAppClick({ target }) {
      target.className.includes('-freeze') && this.mobileHeaderShow && this.$store.dispatch('ui/DISMISS_MOBILE_HEADER');
    },
  },
  watch: {
    freeze(freeze) {
      if (freeze) {
        console.log('body freeze');
        if (!this.$isServer) {
          this.appScrollCheckPoint = window.scrollY || window.pageYOffset || 0;
        }
        document.body.classList.add('-freeze')
        document.body.style.marginTop = `${-this.appScrollCheckPoint}px`;
        document.body.addEventListener('click', this.handleAppClick);
      } else {
        console.log('body infreeze');
        document.body.removeAttribute("style");
        document.body.classList.remove('-freeze')
        document.body.removeEventListener('click', this.handleAppClick);
        if (!this.$isServer) {
          window.scrollTo(0, this.appScrollCheckPoint);
        }
      }
    },
    showMapOnMobile(show) {
      if (show) {
        document.body.classList.add('-freeze--nl')
      } else {
        document.body.classList.remove('-freeze--nl')
      }
    },
    '$i18n.locale': function() {
      this.$options
      && this.$options.asyncData
      && this.$options.asyncData.call(this, {
        i18n: this.$i18n,
        store: this.$store,
        route: this.$route
      }).catch(error => {
        // Vue does not await a watcher callback's Promise.
        console.error('Unable to update locale data', error.message);
      })
    },
    $route: function(to) {
      this.ensureFullFontForNonHomeRoute(to);
    }
  }
}
</script>

<style lang="scss">
  #app {
    display: flex;
    flex-direction: column;
    min-height: calc(100vh + 100px); /* 100px = .m-footer__ribbon height */
  }

  #main {
    flex: 1 0 auto;
  }

  /* IE 10/11: flex-grow doesn't resolve against min-height, so provide explicit height */
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    #app {
      height: calc(100vh + 100px);
    }
  }

  .routing {
    cursor: progress !important;
    :hover {
      cursor: progress !important;
    }
  }
  .-freeze--nl{
    overflow: hidden;
  }
  // @Hugo - Why? It mess up body -freeze
  // html{
  //   width: 100%;
  //   overflow-x: hidden;
  // }
  .m-btn{
    &--grey{
      background-color: $color-grey;
      color: $color-darkgrey;
      &:hover{
        background-color: $color-primary;
      }
    }
  }
  .black-and-white{
    color: white;
  }
  .m-wrap--page{
    max-width: 1200px;
    margin: auto;
    @media(min-width:1689px){
      max-width:1400px;
    }
    @media(min-width:2000px){
      max-width:1900px;
    }
  }
  .vue-content-placeholders-img{
    height: 10rem !important;
    @media (min-width: $breakpoint-md){
      height: 20rem !important;
    }
    @media (min-width: $breakpoint-lg){
      height: 30rem !important;
    }  
  }
  .m-hero-page{
      // padding: 4rem 0rem;
      .cx-t30{
        
        @media (min-width: $breakpoint-sm){
          @include set-font(3.2);
        }
      }
      & + .cx-sec{
        position: relative;
      }
  }
  .emoji {
			color: transparent;
			background: center / contain no-repeat;
			display: inline-block;
			vertical-align: text-bottom;
			height: 1.5em;
			width: 1.5em;
			margin: 0 .1em;
		}
</style>
